export const VERT = `void main(){ gl_Position = vec4(position, 1.0); }`;

// Real brewed-coffee surface v2 — built like liquid, not a gradient:
// domain-warped viscous currents, two-layer surface with crema ring,
// window-light reflection, fine micro-texture, mid-rise sloshing,
// and a slow vortex once the cup is full.
export const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_progress;
uniform float u_dive;
uniform float u_oct;

float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p){
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p){
  float v = 0.0; float a = 0.5;
  for(int i = 0; i < 5; i++){
    if(float(i) >= u_oct) break;
    v += a * noise(p);
    p = p * 2.03 + vec2(11.3, 7.7);
    a *= 0.5;
  }
  return v;
}

void main(){
  vec2 uv = gl_FragCoord.xy / u_res;
  float aspect = u_res.x / u_res.y;
  vec2 p = vec2(uv.x * aspect, uv.y);
  float t = u_time;

  // slosh: waves are strongest mid-rise, settling once full
  float slosh = sin(3.14159 * clamp(u_progress, 0.0, 1.0));
  float amp = 0.018 + 0.030 * slosh;

  // viscous surface — two viscous wave families, slightly out of phase
  float w = (fbm(vec2(p.x * 1.6, t * 0.12)) - 0.5) * amp * 2.0
          + (fbm(vec2(p.x * 4.2 + 3.0, t * 0.07)) - 0.5) * amp;
  float level = u_progress * 1.18 - 0.09;
  float surface = level + w * smoothstep(0.0, 0.12, u_progress);

  // gentle vortex once the cup is full — the surface breathes
  float fullness = smoothstep(0.97, 1.0, u_progress) * (1.0 - u_dive);
  vec2 c = uv - vec2(0.5, 0.45);
  float r0 = length(c);
  float ang = fullness * 0.25 * exp(-r0 * 2.0) * sin(t * 0.35);
  float ca = cos(ang), sa = sin(ang);
  vec2 pv = vec2(c.x * ca - c.y * sa, c.x * sa + c.y * ca) + vec2(0.5, 0.45);
  vec2 pw = vec2(pv.x * aspect, pv.y);

  float depth = surface - uv.y;
  vec3 col = vec3(0.0);
  float alpha = 0.0;

  if(depth > 0.0){
    // deep espresso body with light absorption
    vec3 coffee = mix(vec3(0.085, 0.050, 0.030), vec3(0.006, 0.004, 0.003), smoothstep(0.0, 0.55, depth));

    // domain-warped viscous currents
    vec2 q = vec2(fbm(pw * 1.7 + t * 0.030), fbm(pw * 1.7 + vec2(5.2, 1.3) - t * 0.022));
    float swirl = fbm(pw * 2.4 + 1.9 * q + vec2(0.0, -t * 0.05));
    float fine  = fbm(pw * 6.5 - vec2(t * 0.04, t * 0.05));
    coffee += (swirl - 0.5) * vec3(0.050, 0.030, 0.016) * exp(-depth * 2.5)
            + (fine  - 0.5) * vec3(0.016, 0.010, 0.006) * exp(-depth * 4.0);

    // warm absorption band under the surface
    float band = exp(-depth * 14.0);
    coffee += band * vec3(0.24, 0.14, 0.055) * (0.35 + 0.45 * swirl);

    // window-light reflection sliding softly across the surface
    float win = exp(-pow((uv.x - 0.66 - 0.03 * sin(t * 0.11)) * 5.5, 2.0))
              * exp(-pow(depth * 9.0, 2.0));
    coffee += win * vec3(0.55, 0.38, 0.17) * (0.25 + 0.35 * fine);

    // sharp tiny glints — gloss of a fresh surface
    float g = noise(vec2(pw.x * 2.2 - t * 0.05, uv.y * 7.0));
    float spec = pow(max(g - 0.62, 0.0) / 0.38, 4.0);
    coffee += spec * vec3(0.6, 0.42, 0.19) * exp(-depth * 10.0) * 0.5;

    // crema ring + clustered bubbles hugging the leading edge
    float cremaZone = smoothstep(0.030, 0.0, depth);
    float fn = fbm(pw * 26.0 + vec2(t * 0.09, -t * 0.05));
    float crema = smoothstep(0.52, 0.72, fn) * cremaZone;
    coffee = mix(coffee, vec3(0.55, 0.41, 0.25), crema * 0.5);
    vec2 bc = floor(pw * 34.0 + vec2(0.0, t * 0.15));
    float bh = hash(bc);
    vec2 bpos = fract(pw * 34.0 + vec2(0.0, t * 0.15)) - 0.5;
    float bub = smoothstep(0.10 + 0.08 * bh, 0.03, length(bpos)) * step(0.78, bh);
    coffee = mix(coffee, vec3(0.62, 0.48, 0.30), bub * cremaZone * 0.6);

    col = coffee;
    alpha = 1.0;
  }

  // meniscus — a fine luminous lip at the very edge
  float edge = exp(-abs(depth) * 220.0);
  col += edge * vec3(0.65, 0.45, 0.18) * 0.30 * smoothstep(0.02, 0.2, u_progress);
  alpha = max(alpha, edge * 0.8);

  // passing through the surface
  float d = u_dive;
  col *= 1.0 - d * 0.45;
  col += vec3(0.42, 0.28, 0.12) * d * exp(-r0 * r0 * 3.5) * 0.22;

  // sparse micro-bubbles drifting up while immersed
  if(d > 0.02 && alpha > 0.5){
    vec2 bp = vec2(p.x * 34.0, (uv.y + t * 0.18) * 34.0);
    vec2 cell = floor(bp);
    float h = hash(cell);
    vec2 pos = fract(bp) - 0.5 + (vec2(h, fract(h * 7.0)) - 0.5) * 0.55;
    float bub2 = smoothstep(0.06, 0.015, length(pos)) * step(0.9, h);
    col += bub2 * vec3(0.4, 0.27, 0.12) * d * 0.35;
  }

  // fine film grain
  col += (hash(gl_FragCoord.xy + fract(t)) - 0.5) * 0.014;

  gl_FragColor = vec4(col, alpha);
}
`;
